/**
 * Reiter *Aufgaben* eines Objekts **und** Bereich *Aufgaben* (Spec 06a, „Was
 * neu entsteht": „Bereich und Reiter teilen sich eine Listenkomponente mit
 * anderer Eingabe — dieselbe Aufteilung wie `ContractList`"). Welche Abfrage
 * läuft (`?scope=` oder die Filter des Bereichs, Spec 39), entscheidet
 * der Aufrufer (Container pro Bildschirm besitzt die Query, Spec 02a-Muster);
 * diese Komponente bekommt nur das Ergebnis.
 *
 * `task-add` erscheint **nur am Objekt** (`canAdd`, von `routes/ObjectDetail.tsx`
 * gesetzt) — dieselbe Kontext-Regel wie `contract-add` (Spec 03, „Reiter Geld
 * — die Vertragsliste"): Anlegen braucht ein festes, nicht wählbares Objekt
 * (`ARCHITECTURE.md` 6.4), das der Bereich *Aufgaben* selbst nicht hat.
 * `routes/Aufgaben.tsx` reicht deshalb `canAdd={false}`.
 *
 * Anlegen und Ändern teilen sich `task-sheet` in **einem** Mount (`sheetState`)
 * statt zweier — pro Bildschirm ist ohnehin nie mehr als ein Sheet offen
 * (dieselbe Beobachtung wie bei `ConfirmationStrip`).
 */

import { useState } from 'react'
import type { ReactNode } from 'react'
import type { InfiniteData, UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import { useUpdateTicket } from '@/features/tasks/api'
import type { TicketRow, TicketsPage } from '@/features/tasks/api'
import { TaskRow } from '@/features/tasks/TaskRow'
import { TaskSheet } from '@/features/tasks/TaskSheet'

type SheetState = { readonly mode: 'create' } | { readonly mode: 'edit'; readonly ticket: TicketRow }

interface TaskListProps {
  readonly query: UseQueryResult<TicketsPage> | UseInfiniteQueryResult<InfiniteData<TicketsPage>>
  /** Das Objekt, an dem eine neue Aufgabe entstünde — auch wenn `canAdd`
   * `false` ist, braucht `TaskSheet` es für den Kopf beim Ändern nicht (der
   * kommt dann aus der jeweiligen Zeile), wohl aber `AssigneeField` für
   * die Kandidatenliste beim Anlegen. */
  readonly subjectId: string
  readonly subjectLabel: string
  readonly canAdd: boolean
  readonly emptyTitle: string
  readonly emptyHint: string
  /** Seit Spec 39: die Aufforderung im Leerzustand, wenn der Aufrufer eine
   * eigene hat — im Bereich *Aufgaben* „Filter zurücksetzen" bei
   * abweichendem Filter (Fachregel 7). Ohne sie bleibt es bei `task-add`. */
  readonly emptyAction?: ReactNode
}

export function TaskList({ query, subjectId, subjectLabel, canAdd, emptyTitle, emptyHint, emptyAction }: TaskListProps) {
  const updateMutation = useUpdateTicket()
  const [sheetState, setSheetState] = useState<SheetState | null>(null)

  function markDone(ticket: TicketRow) {
    void updateMutation.mutateAsync({
      ticketId: ticket.id,
      ifMatch: String(ticket.version),
      body: { status: 'done' },
    })
  }

  const editingTicket = sheetState?.mode === 'edit' ? sheetState.ticket : null
  const accessError = query.error instanceof ApiError && (query.error.isForbidden || query.error.isUnauthenticated)
  const hasReadableData = query.data !== undefined && !accessError
  const pageItems = !hasReadableData ? [] : 'pages' in query.data ? query.data.pages.flatMap((page) => page.items) : query.data.items
  const tickets = [...new Map(pageItems.map((item) => [item.id, item])).values()]
  const pagedQuery = 'fetchNextPage' in query ? query : undefined

  const addButton = canAdd && (
    <button
      type="button"
      data-testid="task-add"
      onClick={() => {
        setSheetState({ mode: 'create' })
      }}
      className="border-border text-text hover:bg-bg rounded-card text-body border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out)"
    >
      + Aufgabe
    </button>
  )

  return (
    <div data-testid="task-list">
      {query.isPending && <SkeletonRows testId="task-list-loading" rows={4} />}

      {(accessError || (query.isError && !hasReadableData)) && (
        <ErrorState
          testId="task-list-error"
          retryTestId="task-list-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {hasReadableData && tickets.length === 0 && (
        <EmptyState testId="task-list-empty" title={emptyTitle} hint={emptyHint} action={emptyAction ?? addButton} />
      )}

      {hasReadableData && tickets.length > 0 && (
        <>
          {canAdd && <div className="flex justify-end px-(--page-x) py-2">{addButton}</div>}
          <ul>
            {tickets.map((ticket) => (
              <TaskRow
                key={ticket.id}
                ticket={ticket}
                onOpen={() => {
                  setSheetState({ mode: 'edit', ticket })
                }}
                onDone={() => {
                  markDone(ticket)
                }}
              />
            ))}
          </ul>
          {pagedQuery?.hasNextPage && (
            <button
              type="button"
              data-testid="object-facet-aufgaben-more"
              disabled={pagedQuery.isFetchingNextPage}
              onClick={() => {
                void pagedQuery.fetchNextPage()
              }}
              className="border-border text-text m-2 self-start border px-3"
            >
              Mehr laden
            </button>
          )}
        </>
      )}

      <TaskSheet
        open={sheetState !== null}
        onOpenChange={(open) => {
          if (!open) setSheetState(null)
        }}
        subjectId={subjectId}
        subjectLabel={subjectLabel}
        ticket={editingTicket}
      />
    </div>
  )
}
